import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleCheck, FileSpreadsheet, Loader2 } from "lucide-react";
import {
  CAMPAIGN_CSV_INGEST_LIMITS,
  type CampaignAudienceUploadDto,
  type CampaignAudienceUploadPreviewResponse,
  type ConfigureCampaignAudienceUploadRequest,
} from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import { campaignAudienceWorksheetNames } from "./campaigns-errors.js";
import { cn } from "../../lib/utils.js";
import { CampaignCountrySelect } from "./CampaignCountrySelect.js";
import { CampaignAudienceIssuesList } from "./CampaignAudienceIssuesList.js";
import {
  CAMPAIGN_DEFAULT_COUNTRY_ISO,
  buildCsvConfigureRequest,
  campaignCsvWizardStep,
  csvDefaultCountryVisible,
  csvShowsPhoneColumn,
  isCampaignAudienceUploadFile,
  resetFileInputValue,
  summaryReasonEntries,
} from "./campaigns-presentation.js";

const WIZARD_STEPS = ["upload", "configure", "validate"] as const;

export function CampaignCsvPanel({
  upload,
  preview,
  uploading,
  uploadProgress,
  polling,
  canMutate,
  onUpload,
  onConfigure,
  onRemove,
  busy,
}: {
  upload: CampaignAudienceUploadDto | null;
  preview: CampaignAudienceUploadPreviewResponse | null;
  uploading: boolean;
  uploadProgress: number | null;
  polling: boolean;
  canMutate: boolean;
  onUpload: (file: File, worksheet?: string) => Promise<void>;
  onConfigure: (body: ConfigureCampaignAudienceUploadRequest) => Promise<void>;
  onRemove: () => Promise<void>;
  busy?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [sheetNames, setSheetNames] = useState<string[] | null>(null);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [phoneColumnKey, setPhoneColumnKey] = useState("");
  const [defaultCountryIso, setDefaultCountryIso] = useState(CAMPAIGN_DEFAULT_COUNTRY_ISO);
  const [hasHeader, setHasHeader] = useState<boolean | null>(null);
  const step = campaignCsvWizardStep(upload);
  const showCountry = csvDefaultCountryVisible(upload);

  useEffect(() => {
    if (!upload) return;
    setPhoneColumnKey(upload.phoneColumnKey ?? upload.phoneColumnSuggested ?? "");
    setDefaultCountryIso(upload.defaultCountryIso ?? CAMPAIGN_DEFAULT_COUNTRY_ISO);
    setHasHeader(upload.hasHeader);
  }, [upload]);

  const headers = upload?.headers?.headers ?? [];
  const suggested = upload?.phoneColumnSuggested ?? null;
  const processing = polling || upload?.status === "UPLOADED" || upload?.status === "PARSING";
  const showPhoneColumn = upload ? csvShowsPhoneColumn(upload) && headers.length > 0 : false;
  const showCountryControl = showCountry && canMutate;

  const openFilePicker = () => {
    const input = inputRef.current;
    if (!input) return;
    resetFileInputValue(input);
    input.click();
  };

  const pickFile = async (file: File | undefined, worksheet?: string) => {
    setLocalError(null);
    if (!canMutate) return;
    if (!file) return;
    if (!isCampaignAudienceUploadFile(file)) {
      setSheetNames(null);
      setPendingFile(null);
      setLocalError(t("campaigns.csv.invalidType"));
      return;
    }
    if (file.size > CAMPAIGN_CSV_INGEST_LIMITS.maxFileBytes) {
      setLocalError(t("campaigns.errors.fileTooLarge"));
      return;
    }
    if (!worksheet) {
      setSheetNames(null);
      setPendingFile(null);
    }
    try {
      await onUpload(file, worksheet);
      setSheetNames(null);
      setPendingFile(null);
    } catch (error) {
      const names = campaignAudienceWorksheetNames(error);
      if (!names) throw error;
      setPendingFile(file);
      setSheetNames(names);
      setSelectedSheet((current) => (current.length > 0 && names.includes(current) ? current : names[0]!));
    }
  };

  const submitConfig = async () => {
    await onConfigure(
      buildCsvConfigureRequest({
        defaultCountryIso,
        phoneColumnKey,
        hasHeader,
      }),
    );
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col" data-testid="campaign-csv-panel">
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="sr-only"
        tabIndex={-1}
        aria-label={t("campaigns.csv.chooseFile")}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          resetFileInputValue(event.currentTarget);
          void pickFile(file);
        }}
      />
      <ol
        className="flex flex-wrap gap-2 px-6 pt-2"
        data-testid="campaign-csv-wizard-steps"
        aria-label={t("campaigns.csv.wizardTitle")}
      >
        {WIZARD_STEPS.map((item, index) => (
          <li
            key={item}
            aria-current={item === step ? "step" : undefined}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[0.7rem] font-medium",
              item === step
                ? "border-accent/30 bg-accent/10 text-foreground"
                : "border-line/70 text-muted",
            )}
          >
            <span className="tabular-nums">{index + 1}</span>
            {t(`campaigns.csv.wizard.${item}`)}
          </li>
        ))}
      </ol>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {sheetNames && pendingFile ? (
          <div
            className="grid gap-3 rounded-lg border border-line bg-surface px-4 py-4"
            data-testid="campaign-excel-worksheet"
          >
            <div className="grid gap-1">
              <p className="m-0 text-[0.875rem] font-medium">{t("campaigns.csv.worksheetTitle")}</p>
              <p className="m-0 text-[0.75rem] text-muted">{t("campaigns.csv.worksheetHelp")}</p>
            </div>
            <label className="grid gap-1 text-[0.75rem] font-medium" htmlFor="campaign-excel-worksheet-control">
              {t("campaigns.csv.worksheetLabel")}
              <select
                id="campaign-excel-worksheet-control"
                value={selectedSheet}
                onChange={(event) => setSelectedSheet(event.target.value)}
                disabled={uploading || busy}
                data-testid="campaign-excel-worksheet-select"
                className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-[0.8125rem] font-normal"
              >
                {sheetNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                disabled={uploading || busy}
                onClick={() => {
                  setSheetNames(null);
                  setPendingFile(null);
                  openFilePicker();
                }}
              >
                {t("campaigns.csv.worksheetChange")}
              </Button>
              <Button
                type="button"
                disabled={uploading || busy || selectedSheet.length === 0}
                onClick={() => {
                  void pickFile(pendingFile, selectedSheet);
                }}
              >
                {t("campaigns.csv.worksheetContinue")}
              </Button>
            </div>
          </div>
        ) : step === "upload" || !upload ? (
          <div
            role="button"
            tabIndex={canMutate && !uploading ? 0 : -1}
            className={cn(
              "grid cursor-pointer justify-items-center gap-1.5 rounded-lg border border-dashed border-line bg-surface px-4 py-6 text-center outline-none",
              "hover:border-accent/50 hover:bg-accent/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/50",
              dragOver && "border-accent bg-accent/5",
              (!canMutate || uploading) && "cursor-default opacity-70",
            )}
            data-testid="campaign-csv-dropzone"
            onClick={() => {
              if (!canMutate || uploading) return;
              openFilePicker();
            }}
            onKeyDown={(event) => {
              if (!canMutate || uploading) return;
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              openFilePicker();
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(false);
              void pickFile(event.dataTransfer.files[0]);
            }}
          >
            <FileSpreadsheet size={22} className="text-muted" aria-hidden="true" />
            <p className="m-0 text-[0.875rem] font-medium">{t("campaigns.csv.dropTitle")}</p>
            <p className="m-0 max-w-sm text-[0.75rem] text-muted">{t("campaigns.csv.dropSupport")}</p>
            <p className="m-0 text-[0.7rem] text-subtle">{t("campaigns.csv.limits")}</p>
          </div>
        ) : null}

        {uploading ? (
          <p className="mt-3 flex items-center gap-2 text-[0.8125rem] text-muted" aria-live="polite">
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            {t("campaigns.csv.uploading", { percent: uploadProgress ?? 0 })}
          </p>
        ) : null}
        {localError ? (
          <p className="mt-3 text-[0.8125rem] text-error" role="alert">
            {localError}
          </p>
        ) : null}

        {upload ? (
          <div className="mt-3 grid gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="m-0 text-[0.875rem] font-semibold">{upload.originalFileName}</p>
                <p className="m-0 text-[0.75rem] text-muted">
                  {t(`campaigns.csv.status.${upload.status}`)}
                </p>
              </div>
              {canMutate && upload.status !== "DELETED" ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy || uploading}
                    onClick={openFilePicker}
                  >
                    {t("campaigns.csv.replace")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => void onRemove()}
                  >
                    {t("campaigns.csv.remove")}
                  </Button>
                </div>
              ) : null}
            </div>

            {processing ? (
              <p className="flex items-center gap-2 text-[0.8125rem] text-muted" aria-live="polite">
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                {t("campaigns.csv.processing")}
              </p>
            ) : null}

            {upload.status === "FAILED" ? (
              <p className="text-[0.8125rem] text-error" role="alert">
                {upload.errorSummary
                  ? t("campaigns.csv.failedWithSummary", { summary: upload.errorSummary })
                  : t("campaigns.csv.failed")}
              </p>
            ) : null}
            {upload.status === "EXPIRED" ? (
              <p className="text-[0.8125rem] text-warning">{t("campaigns.csv.expired")}</p>
            ) : null}
            {upload.status === "CONSUMED" ? (
              <p className="text-[0.8125rem] text-muted">{t("campaigns.csv.consumed")}</p>
            ) : null}

            {step === "configure" && !processing ? (
              <div className="grid gap-3" data-testid="campaign-csv-configure">
                {headers.length > 0 ? (
                  <div className="grid gap-2">
                    <p className="m-0 text-[0.75rem] font-medium text-muted">
                      {t("campaigns.csv.columnsFound", { count: headers.length })}
                    </p>
                    <ul
                      className="m-0 flex list-none flex-wrap gap-1.5 p-0"
                      data-testid="campaign-csv-detected-columns"
                    >
                      {headers.map((header) => (
                        <li
                          key={header.key}
                          className="rounded-md border border-line/70 bg-surface-muted px-2 py-1 text-[0.75rem] text-foreground"
                        >
                          {header.displayName}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {upload.headerConfirmationRequired ? (
                  <fieldset className="grid gap-1.5">
                    <legend className="text-[0.8125rem] font-medium">
                      {t("campaigns.csv.firstRow")}
                    </legend>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <label className="flex min-h-9 items-center gap-2 text-[0.8125rem]">
                        <input
                          type="radio"
                          name="campaign-csv-header"
                          checked={hasHeader === true}
                          onChange={() => setHasHeader(true)}
                        />
                        {t("campaigns.csv.firstRowHeader")}
                      </label>
                      <label className="flex min-h-9 items-center gap-2 text-[0.8125rem]">
                        <input
                          type="radio"
                          name="campaign-csv-header"
                          checked={hasHeader === false}
                          onChange={() => setHasHeader(false)}
                        />
                        {t("campaigns.csv.firstRowPhone")}
                      </label>
                    </div>
                  </fieldset>
                ) : null}

                {showPhoneColumn || showCountryControl ? (
                  <div
                    className={cn(
                      "grid max-w-xl gap-3",
                      showPhoneColumn && showCountryControl && "sm:grid-cols-2",
                    )}
                    data-testid="campaign-csv-config-controls"
                  >
                    {showPhoneColumn ? (
                      <div className="grid content-start gap-1">
                        <label
                          htmlFor="campaign-csv-phone-column-control"
                          className="flex items-center gap-2 text-[0.75rem] font-medium"
                        >
                          {t("campaigns.csv.phoneColumn")}
                          <span className="font-normal text-muted">{t("campaigns.csv.phoneColumnRequired")}</span>
                        </label>
                        <select
                          id="campaign-csv-phone-column-control"
                          value={phoneColumnKey}
                          onChange={(event) => setPhoneColumnKey(event.target.value)}
                          data-testid="campaign-csv-phone-column"
                          className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-[0.8125rem]"
                        >
                          <option value="">{t("campaigns.csv.phoneColumnPlaceholder")}</option>
                          {headers.map((header) => (
                            <option key={header.key} value={header.key}>
                              {header.displayName}
                            </option>
                          ))}
                        </select>
                        {suggested ? (
                          <p className="m-0 text-[0.75rem] text-muted">
                            {t("campaigns.csv.phoneColumnSuggested", {
                              column:
                                headers.find((header) => header.key === suggested)?.displayName ??
                                suggested,
                            })}
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    {showCountryControl ? (
                      <div
                        className="grid content-start gap-1"
                        data-testid="campaign-csv-default-country"
                      >
                        <label
                          htmlFor="campaign-csv-default-country-control"
                          className="text-[0.75rem] font-medium"
                        >
                          {t("campaigns.csv.defaultCountry")}
                        </label>
                        <CampaignCountrySelect
                          id="campaign-csv-default-country-control"
                          value={defaultCountryIso}
                          onChange={setDefaultCountryIso}
                          disabled={busy}
                        />
                        <p className="m-0 text-[0.75rem] text-muted">
                          {t("campaigns.csv.countryHelp")}
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {canMutate ? (
                  <div className="grid justify-items-end gap-1">
                    {upload.phoneColumnSelectionRequired && phoneColumnKey.length === 0 ? (
                      <p className="m-0 text-[0.75rem] text-muted">{t("campaigns.csv.validateDisabledPhone")}</p>
                    ) : null}
                    {upload.headerConfirmationRequired && hasHeader == null ? (
                      <p className="m-0 text-[0.75rem] text-muted">{t("campaigns.csv.validateDisabledHeader")}</p>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      disabled={
                        busy ||
                        (upload.phoneColumnSelectionRequired && phoneColumnKey.length === 0) ||
                        (upload.headerConfirmationRequired && hasHeader == null)
                      }
                      onClick={() => void submitConfig()}
                    >
                      {t("campaigns.csv.validateFile")}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {step === "validate" && preview?.summary ? (
              <div className="grid gap-2.5" data-testid="campaign-csv-preview">
                {upload.status === "READY" ? (
                  <div className="flex flex-wrap items-start gap-2" data-testid="campaign-csv-ready">
                    <CircleCheck size={16} className="mt-0.5 shrink-0 text-success" aria-hidden="true" />
                    <div className="grid min-w-0 gap-0.5">
                      <p className="m-0 text-[0.875rem] font-semibold">{t("campaigns.csv.readyTitle")}</p>
                      <p className="m-0 text-[0.75rem] text-muted">
                        {t("campaigns.csv.readyValid", { count: preview.summary.validRowCount })}
                        {preview.summary.invalidRowCount > 0
                          ? ` · ${t("campaigns.csv.readyAttention", { count: preview.summary.invalidRowCount })}`
                          : ""}
                      </p>
                    </div>
                  </div>
                ) : null}
                <dl
                  className="m-0 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-line/70 bg-surface px-3 py-2 text-[0.75rem] sm:grid-cols-5"
                  data-testid="campaign-csv-metrics"
                >
                  <div className="flex items-baseline justify-between gap-2 sm:grid">
                    <dt className="text-muted">{t("campaigns.csv.sourceRows")}</dt>
                    <dd className="m-0 font-medium tabular-nums">{preview.summary.sourceRowCount}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 sm:grid">
                    <dt className="text-muted">{t("campaigns.csv.valid")}</dt>
                    <dd className="m-0 font-medium tabular-nums">{preview.summary.validRowCount}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 sm:grid">
                    <dt className="text-muted">{t("campaigns.csv.invalid")}</dt>
                    <dd className="m-0 font-medium tabular-nums">{preview.summary.invalidRowCount}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 sm:grid">
                    <dt className="text-muted">{t("campaigns.csv.duplicates")}</dt>
                    <dd className="m-0 font-medium tabular-nums">{preview.summary.duplicateRowCount}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 sm:grid">
                    <dt className="text-muted">{t("campaigns.csv.conflicts")}</dt>
                    <dd className="m-0 font-medium tabular-nums">{preview.summary.conflictRowCount}</dd>
                  </div>
                </dl>
                <div data-testid="campaign-csv-issues">
                  <CampaignAudienceIssuesList
                    reasons={summaryReasonEntries(preview.summary.byReason)}
                    samples={preview.issueSamples.slice(0, 8)}
                  />
                </div>
                {preview.summary.conflictRowCount > 0 ? (
                  <p className="m-0 text-[0.75rem] text-muted">{t("campaigns.csv.conflictHelp")}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
